import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import AddScenarioSidebar from 'components/AddScenarioSidebar/AddScenarioSidebar';
import Button from 'components/Button/Button';
import ScenarioShowcase from 'components/ScenarioShowcase/ScenarioShowcase';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { MAX_ANALYSIS_SCENARIOS } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';

import type { IScenarioFormInitialData, IScenarioFormSubmitData } from 'components/AddScenarioSidebar/interfaces/IAddScenarioSidebar';
import type IValueChainMapAnalysisScenario from 'store/slices/valueChainMap/interfaces/IValueChainMapAnalysisScenario';
import type IAnalysisSidebarSetupTab from './interfaces/IAnalysisSidebarSetupTab';

import styles from './scss/AnalysisSidebarSetupTab.module.scss';

function AnalysisSidebarSetupTab({
  scenarios,
  isAnalysisDetailsLoading,
  boardEntities,
  isSavingScenario,
  activeScenarioId,
  activeViewScenarioId,
  onSaveScenario,
  onUpdateScenario,
  onDeleteAnalysisScenario,
  onSelectScenario,
  onViewScenario,
  onStartImpactAnalysis,
  isStartImpactAnalysisLoading,
  isStartImpactAnalysisDisabled,
  open,
  analysisId,
  onCopyExisting,
  pendingScenarioInitialData,
  onClearPendingScenarioInitialData,
}: IAnalysisSidebarSetupTab): React.JSX.Element {
  const { t } = useTranslation();
  const [isCreatingScenario, setIsCreatingScenario] = useState(false);
  const [editingScenarioId, setEditingScenarioId] = useState<number | null>(null);
  const scenariosScrollContainerRef = useRef<HTMLDivElement>(null);
  const scenarioFormWrapperRef = useRef<HTMLDivElement>(null);
  const isScenarioFormActive = isCreatingScenario || editingScenarioId !== null;

  const mapScenarioToFormInitialData = (
    scenario: IValueChainMapAnalysisScenario,
  ): IScenarioFormInitialData => ({
    name: scenario.name,
    affectedPeriod: scenario.affectedPeriod,
    affectedPeriodUnit: scenario.affectedPeriodUnit,
    productivityImpact: scenario.productivityImpact,
    unitIds: scenario?.units?.map((unit) => unit.id) ?? [],
  });

  const scenarioFormInitialDataById = useMemo(() => {
    const initialDataById = new Map<number, IScenarioFormInitialData>();

    scenarios?.forEach((scenario) => {
      initialDataById.set(scenario.id, mapScenarioToFormInitialData(scenario));
    });

    return initialDataById;
  }, [scenarios]);

  useEffect(() => {
    if (!open) {
      setIsCreatingScenario(false);
      setEditingScenarioId(null);
    }
  }, [open]);

  useEffect(() => {
    setIsCreatingScenario(false);
    setEditingScenarioId(null);
  }, [analysisId]);

  useEffect(() => {
    if (!open || isAnalysisDetailsLoading || !pendingScenarioInitialData) {
      return;
    }

    setEditingScenarioId(null);
    setIsCreatingScenario(true);
  }, [analysisId, isAnalysisDetailsLoading, open, pendingScenarioInitialData]);

  useLayoutEffect(() => {
    if (isAnalysisDetailsLoading) return;
    if (!isScenarioFormActive) return;

    scenarioFormWrapperRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [isAnalysisDetailsLoading, isScenarioFormActive]);

  const handleCancelScenario = () => {
    setIsCreatingScenario(false);
    setEditingScenarioId(null);
    onClearPendingScenarioInitialData?.();
  };

  const handleCreateScenario = () => {
    const scenariosCount = scenarios?.length ?? 0;

    if (scenariosCount >= MAX_ANALYSIS_SCENARIOS) {
      toast.error(t('valueChainMap.maxScenariosReached', { count: MAX_ANALYSIS_SCENARIOS }));
      return;
    }

    setEditingScenarioId(null);
    setIsCreatingScenario(true);
  };

  const handleEditScenario = (scenarioId: number) => {
    if (activeViewScenarioId !== null) {
      onViewScenario(activeViewScenarioId);
    }

    setIsCreatingScenario(false);
    setEditingScenarioId(scenarioId);
  };

  const handleDeleteScenarioClick = () => {
    if (activeViewScenarioId === null) return;

    onViewScenario(activeViewScenarioId);
  };

  const handleSaveScenarioForm = async (data: IScenarioFormSubmitData) => {
    await onSaveScenario(data);
    setIsCreatingScenario(false);
  };

  const handleSaveEditedScenarioForm = async (
    scenarioId: number,
    data: IScenarioFormSubmitData,
  ) => {
    await onUpdateScenario(scenarioId, data);
    setEditingScenarioId(null);
  };

  return (
    <>
      <div className={styles.content}>
        <div
          className={styles.scenariosContainer}
          ref={scenariosScrollContainerRef}
        >
          {isAnalysisDetailsLoading ? (
            <div className={styles.loaderWrapper} role="status" aria-live="polite">
              <SmallLoader size={30} />
            </div>
          ) : null}
          {!isAnalysisDetailsLoading && isCreatingScenario ? (
            <div ref={scenarioFormWrapperRef}>
              <AddScenarioSidebar
                boardEntities={boardEntities}
                isSaving={isSavingScenario}
                initialData={pendingScenarioInitialData ?? undefined}
                onCancel={handleCancelScenario}
                onSave={handleSaveScenarioForm}
                scrollContainerRef={scenariosScrollContainerRef}
              />
            </div>
          ) : null}
          {!isAnalysisDetailsLoading ? scenarios?.map((scenario) => {
            if (scenario.id === editingScenarioId) {
              return (
                <div key={`edit-${scenario.id}`} ref={scenarioFormWrapperRef}>
                  <AddScenarioSidebar
                    boardEntities={boardEntities}
                    isSaving={isSavingScenario}
                    isEditing
                    initialData={
                      scenarioFormInitialDataById.get(scenario.id)
                      ?? mapScenarioToFormInitialData(scenario)
                    }
                    onCancel={handleCancelScenario}
                    onSave={(data) => handleSaveEditedScenarioForm(scenario.id, data)}
                    scrollContainerRef={scenariosScrollContainerRef}
                  />
                </div>
              );
            }
            return (
              <ScenarioShowcase
                key={scenario.id}
                scenario={scenario}
                boardEntities={boardEntities}
                isActive={activeScenarioId === scenario.id}
                isViewActive={activeViewScenarioId === scenario.id}
                isViewEditDisabled={editingScenarioId !== null}
                onSelect={() => onSelectScenario(scenario.id)}
                onView={() => onViewScenario(scenario.id)}
                onEdit={() => handleEditScenario(scenario.id)}
                onDeleteClick={handleDeleteScenarioClick}
                onDelete={() => { onDeleteAnalysisScenario(scenario.id); }}
              />
            );
          }) : null}
        </div>
        {!isAnalysisDetailsLoading && !isCreatingScenario && editingScenarioId == null ? (
          <div className={styles.scenarioActions}>
            <Button
              title={t('valueChainMap.createNewScenario')}
              labelKey="valueChainMap.createNewScenario"
              variant={BUTTON_VARIANT.cancel}
              className={styles.createScenarioButton}
              startIcon={<PlusIcon aria-hidden="true" />}
              onClick={handleCreateScenario}
            />
            <Button
              title={t('valueChainMap.copyExisting')}
              variant={BUTTON_VARIANT.cancel}
              className={styles.copyScenarioButton}
              startIcon={<CopyIcon aria-hidden="true" />}
              onClick={onCopyExisting}
            />
          </div>
        ) : null}
      </div>

      <div className={styles.footer}>
        <Button
          title={t('valueChainMap.startImpactAnalysis')}
          labelKey="valueChainMap.startImpactAnalysis"
          variant={BUTTON_VARIANT.primary}
          isLoading={isStartImpactAnalysisLoading}
          disabled={isStartImpactAnalysisDisabled || isScenarioFormActive}
          onClick={onStartImpactAnalysis}
          className={styles.startButton}
        />
      </div>
    </>
  );
}

export default AnalysisSidebarSetupTab;
